import { useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  useCultivationFormData,
  useCultivationFormFieldData,
  useUpdateCultivationFormData
} from "../../../../api/farmGate";
import { errorView, successMessage } from "../../../../helpers/ErrorHandler";
import {
  CULTIVAR_INIT_STEP,
  CultivarSectionRefs,
  FarmGateFormValues,
  FieldDataValues,
  SaveDocumentDataHandle
} from "../types/cultivarForm.types";
import {
  buildCultivationFieldPayload,
  buildCultivationPayload,
  collectCultivationFieldSectionData,
  resolveCreatedFarmGateId,
  resolveCultivationData,
  resolveFieldData
} from "../utils/cultivarFormPayload.utils";
import { getHarvestYearError } from "../utils/cultivarFormValidation.utils";

interface UseCultivarFormSubmitOptions {
  farmGateId: string;
  supplierId: string;
  ingredientId: string;
  ownerId: string;
  sourceId: string;
  nodeItem: any;
  currentFormData: any;
  sectionRefs: CultivarSectionRefs;
  notesRef: React.RefObject<SaveDocumentDataHandle>;
  form: UseFormReturn<FarmGateFormValues>;
  updateNodeItem: (nodeItem: any, savedFarmGateId: string, cultivationData?: Record<string, any>) => any;
  updateDrawingNode: (
    node: any,
    farmGateName?: string,
    options?: { closeModal?: boolean }
  ) => void;
  onFarmGateCreated?: (farmGateId: string) => void;
}

export const useCultivarFormSubmit = ({
  farmGateId,
  supplierId,
  ingredientId,
  ownerId,
  sourceId,
  nodeItem,
  currentFormData,
  sectionRefs,
  notesRef,
  form,
  updateNodeItem,
  updateDrawingNode,
  onFarmGateCreated
}: UseCultivarFormSubmitOptions) => {
  const {
    mutate: cultivationFormFieldDataMutate,
    isLoading: isCultivationFormFieldDataMutateLoading
  } = useCultivationFormFieldData();

  const { mutate: cultivationFormDataMutate, isLoading: isCultivarFormDataMutateLoading } =
    useCultivationFormData();

  const { mutate: updateCultivationDataMutate, isLoading: isUpdateCultivationDataMutateLoading } =
    useUpdateCultivationFormData();

  const persistDrawingNode = useCallback(
    async (
      cFormData: Record<string, any>,
      mergedValues: Record<string, any>,
      savedFarmGateId: string,
      closeModal = false
    ) => {
      try {
        await notesRef.current?.saveDocumentAndNote(savedFarmGateId);
        onFarmGateCreated?.(savedFarmGateId);

        const fieldData = await resolveFieldData(sectionRefs, currentFormData);
        const sectionData = collectCultivationFieldSectionData(sectionRefs, currentFormData);
        const cultivationFieldFormValues = buildCultivationFieldPayload(
          savedFarmGateId,
          mergedValues,
          fieldData,
          sectionData,
          currentFormData
        );

        cultivationFormFieldDataMutate(cultivationFieldFormValues);

        const updatedNodeItem = updateNodeItem(nodeItem, savedFarmGateId, mergedValues);
        updateDrawingNode(updatedNodeItem, cFormData.farmGateName, { closeModal });
      } catch (error) {
        console.error("Error saving document and note:", error);
      }
    },
    [
      notesRef,
      sectionRefs,
      currentFormData,
      onFarmGateCreated,
      cultivationFormFieldDataMutate,
      updateNodeItem,
      nodeItem,
      updateDrawingNode
    ]
  );

  const onSubmitHandler = useCallback(
    async (formValues: FarmGateFormValues, closeModalOnSuccess = false) => {
      const cultivationData = await resolveCultivationData(sectionRefs, currentFormData);
      if (!cultivationData) {
        errorView("Please complete cultivar details");
        return;
      }

      // Field requirements (yield, growth area) only apply once the farm gate exists;
      // the first save just creates the farm gate so those sections can be filled afterwards
      const fieldData =
        (await resolveFieldData(sectionRefs, currentFormData)) ??
        (farmGateId ? null : ({} as FieldDataValues));
      if (!fieldData) {
        errorView("Please complete field details");
        return;
      }

      const sectionData = collectCultivationFieldSectionData(sectionRefs, currentFormData);
      const landUsageChangeStatus =
        sectionRefs.landUse.current?.getLanduseStatus?.() ??
        currentFormData?.landUsageChange ??
        "no";

      const cultivation = buildCultivationPayload({
        formValues,
        supplierId,
        ingredientId,
        ownerId,
        sourceId,
        farmGateId,
        currentFormData,
        cultivationData,
        fieldData,
        sectionData,
        landUsageChangeStatus,
        userCompletedStage: CULTIVAR_INIT_STEP
      });

      if (!farmGateId) {
        cultivationFormDataMutate(cultivation, {
          onSuccess: async (data: any) => {
            const savedFarmGateId = resolveCreatedFarmGateId(data);
            if (!savedFarmGateId) {
              errorView("Cultivation saved but farm gate id was missing");
              return;
            }

            await persistDrawingNode(cultivation, cultivation, savedFarmGateId, closeModalOnSuccess);
            successMessage("Cultivation created successfully");
          }
        });
        return;
      }

      updateCultivationDataMutate(cultivation, {
        onSuccess: async () => {
          await persistDrawingNode(cultivation, cultivation, farmGateId, closeModalOnSuccess);
          successMessage("Cultivation updated successfully");
        }
      });
    },
    [
      sectionRefs,
      currentFormData,
      supplierId,
      ingredientId,
      ownerId,
      sourceId,
      farmGateId,
      cultivationFormDataMutate,
      updateCultivationDataMutate,
      persistDrawingNode
    ]
  );

  const validateHarvestYear = useCallback(async (): Promise<boolean> => {
    const fieldData = await resolveFieldData(sectionRefs, currentFormData);
    const validationMessage = getHarvestYearError(fieldData);

    if (validationMessage) {
      errorView(validationMessage);
      return false;
    }

    return true;
  }, [sectionRefs, currentFormData]);

  const handleBottomUpdate = useCallback(async () => {
    const isFarmGateValid = await form.trigger();
    if (!isFarmGateValid) return;

    const isHarvestYearValid = await validateHarvestYear();
    if (!isHarvestYearValid) return;

    await onSubmitHandler(form.getValues(), true);
  }, [form, onSubmitHandler, validateHarvestYear]);

  const isSubmitting =
    isCultivarFormDataMutateLoading ||
    isCultivationFormFieldDataMutateLoading ||
    isUpdateCultivationDataMutateLoading;

  return {
    onSubmitHandler,
    handleBottomUpdate,
    isSubmitting
  };
};
